(() => {
  const LETTERS_ONLY = /[^A-Z]/g;

  const sanitizeInitials = (value) =>
    String(value || '')
      .toUpperCase()
      .replace(LETTERS_ONLY, '')
      .slice(0, 2);

  const formatDate = (date) =>
    date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

  const addDays = (days) => {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() + Number(days || 0));
    return date;
  };

  const cartJsUrl = () => {
    const cartUrl = window.routes?.cart_url || '/cart';
    return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
  };

  class CoupleInitialsProduct extends HTMLElement {
    connectedCallback() {
      if (this.nciBound) return;
      this.nciBound = true;
      this.submitting = false;

      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      this.initials = this.querySelector('[data-nci-initials]');
      this.initialsError = this.querySelector('[data-nci-initials-error]');
      this.counter = this.querySelector('.nci-counter');
      this.variantInput = this.querySelector('[data-nci-variant-id]');
      this.atcButton = this.querySelector('[data-nci-atc]');
      this.atcLabel = this.atcButton?.querySelector('span');
      this.colourName = this.querySelector('[data-nci-colour-name]');
      this.formError = this.querySelector('.product-form__error-message-wrapper');
      this.formErrorText = this.querySelector('.product-form__error-message');

      this.bindGallery();
      this.bindInitials();
      this.bindColours();
      this.bindSizes();
      this.fillTimeline();
      this.updateAtcState();

      this.form?.addEventListener('submit', this.onSubmit.bind(this), true);
    }

    bindGallery() {
      const gallery = this.querySelector('[data-nci-gallery]');
      if (!gallery) return;
      gallery.querySelectorAll('[data-media-target]').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const id = thumb.getAttribute('data-media-target');
          gallery.querySelectorAll('.nci-gallery__slide').forEach((slide) => {
            const active = slide.getAttribute('data-media-id') === id;
            slide.classList.toggle('is-active', active);
            slide.hidden = !active;
          });
          gallery.querySelectorAll('[data-media-target]').forEach((button) => {
            const active = button === thumb;
            button.classList.toggle('is-active', active);
            button.setAttribute('aria-selected', active ? 'true' : 'false');
          });
        });
      });
    }

    bindInitials() {
      if (!this.initials) return;
      const apply = () => {
        const next = sanitizeInitials(this.initials.value);
        if (this.initials.value !== next) this.initials.value = next;
        if (this.counter) this.counter.textContent = `${next.length}/2`;
        this.updateAtcState();
        if (next.length === 2) this.setInitialsError(false);
      };
      this.initials.addEventListener('input', apply);
      this.initials.addEventListener('blur', () => {
        apply();
        if (this.initials.value && this.initials.value.length < 2) this.setInitialsError(true);
      });
      apply();
    }

    bindColours() {
      this.querySelectorAll('[data-nci-colour]').forEach((input) => {
        input.addEventListener('change', () => {
          if (this.colourName) this.colourName.textContent = input.value;
        });
      });
    }

    bindSizes() {
      this.querySelectorAll('[data-nci-size]').forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
          this.querySelectorAll('[data-nci-size]').forEach((item) => {
            item.classList.toggle('is-selected', item === button);
            item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
          });
          if (this.variantInput) {
            this.variantInput.value = button.dataset.variantId;
            this.variantInput.disabled = button.dataset.available !== 'true';
          }
          this.updatePrices(button.dataset.price, button.dataset.compare);
          this.updateAtcState();
        });
      });
    }

    fillTimeline() {
      const timeline = this.querySelector('[data-nci-timeline]');
      if (!timeline) return;
      const makeDays = Number(timeline.dataset.makeDays || this.dataset.makeDays || 5);
      const totalDays = Number(timeline.dataset.totalDays || this.dataset.totalDays || 10);
      const today = timeline.querySelector('[data-nci-date="today"]');
      const ready = timeline.querySelector('[data-nci-date="ready"]');
      const eta = timeline.querySelector('[data-nci-date="eta"]');
      if (today) today.textContent = formatDate(new Date());
      if (ready) ready.textContent = `Approx. ${formatDate(addDays(makeDays))}`;
      if (eta) eta.textContent = `Approx. ${formatDate(addDays(totalDays))}`;
    }

    updatePrices(price, compare) {
      this.querySelectorAll('[data-nci-price-current]').forEach((node) => {
        if (price) node.textContent = price;
      });
      this.querySelectorAll('[data-nci-price-compare]').forEach((node) => {
        if (compare) {
          node.textContent = compare;
          node.hidden = false;
        } else {
          node.textContent = '';
          node.hidden = true;
        }
      });
    }

    selectedSizeButton() {
      return this.querySelector('[data-nci-size].is-selected') || this.querySelector('[data-nci-size]:not([disabled])');
    }

    initialsAreValid() {
      return sanitizeInitials(this.initials?.value).length === 2;
    }

    variantAvailable() {
      const selected = this.selectedSizeButton();
      if (selected) return selected.dataset.available === 'true';
      return this.variantInput ? !this.variantInput.disabled : false;
    }

    setInitialsError(show) {
      if (!this.initialsError || !this.initials) return;
      this.initialsError.hidden = !show;
      this.initials.setAttribute('aria-invalid', show ? 'true' : 'false');
    }

    showFormError(message) {
      if (!this.formError || !this.formErrorText) return;
      this.formErrorText.textContent = message || '';
      this.formError.toggleAttribute('hidden', !message);
    }

    updateAtcState() {
      if (!this.atcButton) return;
      const available = this.variantAvailable();
      const valid = this.initialsAreValid();
      const disable = !available || !valid || this.submitting;
      this.atcButton.disabled = disable;
      if (this.submitting) return;
      if (this.atcLabel) {
        this.atcLabel.textContent = available
          ? this.dataset.atcLabel || 'Add to Cart'
          : this.dataset.soldOut || 'Sold out';
      }
    }

    setSubmitting(isSubmitting) {
      this.submitting = Boolean(isSubmitting);
      this.atcButton?.classList.toggle('loading', this.submitting);
      this.atcButton?.setAttribute('aria-busy', this.submitting ? 'true' : 'false');
      const spinner = this.atcButton?.querySelector('.loading__spinner');
      spinner?.classList.toggle('hidden', !this.submitting);
      this.updateAtcState();
    }

    async syncHeaderCount() {
      try {
        const cart = await fetch(cartJsUrl(), { headers: { Accept: 'application/json' } }).then((response) =>
          response.json()
        );
        const count = Number(cart.item_count);
        const icon = document.getElementById('cart-icon-bubble');
        document.querySelectorAll('.nh-cart-count').forEach((node) => {
          node.textContent = String(count);
          node.hidden = count < 1;
        });
        if (!icon || !Number.isFinite(count)) return;
        let badge = icon.querySelector('.nh-cart-count');
        if (count < 1) {
          badge?.remove();
          icon.querySelector('.cart-count-bubble')?.remove();
          return;
        }
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'nh-cart-count';
          icon.appendChild(badge);
        }
        badge.hidden = false;
        badge.textContent = String(count);
      } catch (error) {
        /* header count can stay stale */
      }
    }

    getCartComponent() {
      return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    }

    cartSectionIds(cart) {
      if (!cart || typeof cart.getSectionsToRender !== 'function') return [];
      return cart
        .getSectionsToRender()
        .map((section) => section.id)
        .filter(Boolean);
    }

    sectionsHaveHtml(sections) {
      return Boolean(sections && Object.values(sections).some(Boolean));
    }

    async fetchDrawerSectionHtml() {
      const cartUrl = (window.routes?.cart_url || '/cart').replace(/\.js$/, '');
      const urls = [`${cartUrl}?section_id=cart-drawer`];
      const pathname = window.location.pathname;
      if (pathname && pathname !== cartUrl) urls.push(`${pathname}?section_id=cart-drawer`);

      for (const url of urls) {
        const response = await fetch(url);
        if (!response.ok) continue;
        const html = await response.text();
        if (html && /id=["']CartDrawer["']/.test(html)) return html;
      }

      return '';
    }

    async paintCart(cart, response) {
      if (!cart) {
        window.location = window.routes?.cart_url || '/cart';
        return;
      }

      cart.classList.remove('is-empty');
      cart.setActiveElement?.(document.activeElement);

      const drawerId = this.cartSectionIds(cart)[0] || 'cart-drawer';
      let sections = response?.sections;
      if (!this.sectionsHaveHtml(sections)) {
        const html = await this.fetchDrawerSectionHtml();
        if (html) sections = { ...(sections || {}), [drawerId]: html };
      }

      if (this.sectionsHaveHtml(sections) && typeof cart.renderContents === 'function') {
        cart.renderContents({ ...response, sections });
        return;
      }

      if (typeof cart.open === 'function') cart.open();
    }

    async onSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.submitting) return;

      const initials = sanitizeInitials(this.initials?.value);
      if (this.initials) this.initials.value = initials;
      if (initials.length !== 2) {
        this.setInitialsError(true);
        this.initials?.focus();
        this.updateAtcState();
        return;
      }

      if (!this.variantAvailable()) {
        this.showFormError(this.dataset.soldOut || 'Sold out');
        this.updateAtcState();
        return;
      }

      this.showFormError('');
      this.setSubmitting(true);

      const cart = this.getCartComponent();
      try {
        const formData = new FormData(this.form);
        const sectionIds = this.cartSectionIds(cart);
        if (cart && sectionIds.length) {
          formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', (window.routes?.cart_url || '/cart').replace(/\.js$/, ''));
          cart.setActiveElement?.(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const cartAdd = window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
        const httpResponse = await fetch(cartAdd, config);
        const data = await httpResponse.json().catch(() => ({}));

        if (data.status || !httpResponse.ok) {
          throw new Error(data.description || data.message || 'Could not add this product to cart.');
        }

        await this.paintCart(cart, data);
        await this.syncHeaderCount();
      } catch (error) {
        console.error('Neoniify couple initials add to cart failed:', error);
        this.showFormError(error.message || 'Could not add this product to cart.');
      } finally {
        this.setSubmitting(false);
      }
    }
  }

  if (!customElements.get('couple-initials-product')) {
    customElements.define('couple-initials-product', CoupleInitialsProduct);
  }
})();
